import React from "react";
import { Link, useLocation } from "react-router-dom"; // useLocation for detecting active link
import { useTranslation } from "../contexts/I18nProvider";
import {
  Home,
  FileText,
  Shield,
  LogOut,
  User,
  DollarSign,
  Mail,
  Stars,
} from "lucide-react";
import { useLoggedIn } from "../hooks/use-logged-in";
import { cn } from "../lib/utils";

const getNavigationItems = (t) => [
  { to: "/", icon: Home, label: t('components.bottomNav.home') },
  { to: "/polyglot-legacy", icon: Home, label: t('components.navbar.polyglot') },
  {
    to: "/get-started",
    icon: Stars,
    label: t('components.navbar.getStarted'),
    condition: "!isLoggedIn",
    featured: true,
  },
  { to: "/profile", icon: User, label: t('components.bottomNav.profile'), condition: "isLoggedIn" },
  {
    to: "/pricing",
    icon: DollarSign,
    label: t('components.navbar.pricing'),
    condition: "isLoggedIn",
  },
  { to: "/logout", icon: LogOut, label: t('navigation.logout'), condition: "isLoggedIn" },
  { to: "/terms", icon: FileText, hiddenOnMobile: true, label: t('footer.terms') },
  { to: "/privacy", icon: Shield, hiddenOnMobile: true, label: t('footer.privacy') },
  {
    to: `/contact`,
    icon: Mail,
    label: t('components.navbar.contact'),
  },
];

const useIsActive = () => {
  const location = useLocation(); // Get current location/path

  // Function to check if the link is active
  const isActive = (path) => location.pathname === path;

  return isActive;
};

const BottomNav = () => {
  const { t } = useTranslation();
  const { isLoggedIn } = useLoggedIn();
  const navigationItems = getNavigationItems(t);

  return (
    <nav className="fixed z-50 bottom-0 left-0 w-full bg-white border-t border-gray-200 shadow-md">
      <Navigation isLoggedIn={isLoggedIn} navigationItems={navigationItems} />
    </nav>
  );
};

export const Navigation = ({ isLoggedIn, navigationItems }) => {
  return (
    <ul className="flex justify-around items-center lg:gap-2 lg:py-2">
      {navigationItems.map((item, index) => {
        // Skip items based on the condition
        if (item.condition) {
          if (item.condition === "!isLoggedIn" && isLoggedIn) return null;
          if (item.condition === "isLoggedIn" && !isLoggedIn) return null;
        }

        return <BottomNavListItem key={index} item={item} />;
      })}
    </ul>
  );
};

export const BottomNavListItem = ({ item }) => {
  const isActive = useIsActive();
  return (
    <li
      className={cn(
        item.hiddenOnMobile ? "hidden md:grid" : "grid",
        "flex-1 place-items-center lg:rounded-xl transition-colors duration-100 ease-in-out overflow-hidden border border-gray-100",
        isActive(item.to) ? "bg-primary" : "hover:bg-muted/70"
      )}
    >
      <BottomNavLink
        to={item.to}
        icon={item.icon}
        label={item.label}
        featured={item.featured}
      />
    </li>
  );
};

export const BottomNavLink = ({
  className,
  icon,
  label,
  featured,
  ...props
}) => {
  const Icon = icon;
  const isActive = useIsActive()(props.to);

  return (
    <Link
      className={cn(
        `flex flex-col lg:flex-row lg:gap-2 items-center py-2 w-full h-full justify-center text-gray-600 hover:text-[#333333] transition-all duration-100 ease-in-out`,
        isActive
          ? featured
            ? "bg-primary text-primary-foreground  hover:text-primary-foreground"
            : "bg-muted text-gray-900  hover:text-gray-950"
          : featured
          ? "bg-primary/20 text-primary transition-colors hover:bg-primary/10 hover:text-gray-900"
          : "",
        className
      )}
      {...props}
    >
      <Icon className="h-4 lg:h-5 md:w-4 lg:w-5" />
      <span className="text-xs lg:text-sm md:text-md font-medium tracking-wide mt-1">
        {label}
      </span>
    </Link>
  );
};

export default BottomNav;
